// Minimal centralized store for shared state across Stimulus controllers.
// Survives Turbo Frame re-renders because it lives in a plain JS module (singleton).
// Keys listed in _PERSISTED are also written to sessionStorage so they survive
// full page reloads within the same browser tab.

const _PERSISTED = new Set(["comparisonLapId", "comparisonLapLabel"])
const _STORAGE_PREFIX = "ss:"

const _state = new Map()
const _listeners = new Map() // key -> Set<callback>
let _batching = false
let _pendingKeys = new Set()

// Hydrate persisted keys from sessionStorage on module load
for (const key of _PERSISTED) {
  try {
    const raw = sessionStorage.getItem(_STORAGE_PREFIX + key)
    if (raw !== null) _state.set(key, JSON.parse(raw))
  } catch (_) {
    /* ignore */
  }
}

export function get(key) {
  return _state.get(key)
}

export function set(key, value) {
  const prev = _state.get(key)
  if (Object.is(prev, value)) return
  // Shallow array/object equality check for common cases
  if (
    Array.isArray(prev) &&
    Array.isArray(value) &&
    prev.length === value.length &&
    prev.every((v, i) => Object.is(v, value[i]))
  )
    return

  _state.set(key, value)

  // Persist to sessionStorage if this key is in the persisted set
  if (_PERSISTED.has(key)) {
    try {
      if (value == null) sessionStorage.removeItem(_STORAGE_PREFIX + key)
      else sessionStorage.setItem(_STORAGE_PREFIX + key, JSON.stringify(value))
    } catch (_) {
      /* quota exceeded or private mode */
    }
  }

  if (_batching) {
    _pendingKeys.add(key)
  } else {
    _notify(key, value)
  }
}

export function subscribe(key, callback) {
  if (!_listeners.has(key)) _listeners.set(key, new Set())
  _listeners.get(key).add(callback)
  return () => _listeners.get(key)?.delete(callback)
}

export function batch(fn) {
  _batching = true
  _pendingKeys = new Set()
  try {
    fn()
  } finally {
    _batching = false
    for (const key of _pendingKeys) {
      _notify(key, _state.get(key))
    }
    _pendingKeys = new Set()
  }
}

function _notify(key, value) {
  const callbacks = _listeners.get(key)
  if (!callbacks) return
  for (const cb of callbacks) {
    cb(value)
  }
}
